import { Button, PaginationProps, Popconfirm, Space, Table, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import React from 'react';
import { DeleteOutlined, LockOutlined } from '@ant-design/icons';
import './InstallationLocationTable.scss';
import { useTranslation } from '../../i18n/i18n';
import useAuthStore from '../../stores/auth.store';
import EmptyState from '../utils/EmptyState/EmptyState';
import useTenantStore, { ALL_TENANTS_VALUE, TENANT_COL_KEY } from '../../stores/tenant.store';

const { Text } = Typography;

export type LocationTableRow = {
  uuid: string;
  name: string;
  erpId: string;
  direction: string;
  poblation: string;
  installations: string;
  devices: unknown[];
  tenant?: string;
  admin?: string;
};

type InstallationLocationTableProps = {
  data: readonly LocationTableRow[];
  pagination: PaginationProps;
  onChange: (pagination: PaginationProps) => void;
  onRowClick: (record: LocationTableRow) => void;
  handleClick: (record: LocationTableRow) => void;
  loading?: boolean;
};

const InstallationLocationTable = ({ onChange, ...props }: InstallationLocationTableProps) => {
  const { t } = useTranslation('installation-location', { keyPrefix: 'table' });
  const { t: tCommon } = useTranslation('common');
  const isSuperAdminOrCompanyAdmin = useAuthStore((state) => state.isSuperAdminOrCompanyAdmin);
  const isAll = useTenantStore((state) => state.selectedTenant === ALL_TENANTS_VALUE);

  const columns: ColumnsType<LocationTableRow> = [
    {
      title: t('id'),
      dataIndex: 'erpId',
      key: 'erpId',
      render: (text: string) => <Text className={'MainTableText'}>{text}</Text>,
      width: '12%',
    },
    {
      title: t('direction'),
      dataIndex: 'direction',
      key: 'direction',
      width: '20%',
    },
    {
      title: t('poblation'),
      dataIndex: 'poblation',
      width: '15%',
    },
    {
      title: t('instalationLocation'),
      dataIndex: 'installations',
      key: 'installations',
      width: '12%',
    },
    {
      title: t('admin'),
      dataIndex: 'admin',
      key: 'admin',
      width: '15%',
    },
    {
      title: t('tenant'),
      dataIndex: TENANT_COL_KEY,
      key: TENANT_COL_KEY,
      width: '15%',
    },
    {
      title: t('actions'),
      key: 'actions',
      width: '12%',
      render: (_: unknown, record: LocationTableRow) => (
        <Space size="middle" className={'InstallationLocationTable__action'}>
          <Popconfirm
            title={t('deleteTitle')}
            description={
              <>
                {t('deleteDesc1', { name: record.erpId })}
                <br />
                {t('deleteDesc2')}
              </>
            }
            okText={t('deleteOk')}
            okButtonProps={{ danger: true }}
            cancelText={t('deleteCancel')}
            placement={'left'}
            onConfirm={(e) => {
              e?.stopPropagation();
              props.handleClick(record);
            }}
          >
            <Button type={'text'} onClick={(e) => e.stopPropagation()}>
              <DeleteOutlined className={'InstallationLocationTable__delete__icon'} />
            </Button>
          </Popconfirm>
          <Popconfirm
            title={t('blockTitle')}
            description={t('blockDesc')}
            okText={t('blockOk')}
            cancelText={t('blockCancel')}
            placement={'left'}
          >
            <Button type={'text'} onClick={(e) => e.stopPropagation()} style={{ visibility: 'hidden' }}>
              <LockOutlined style={{ color: 'red' }} />
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ].filter((col) => !(col.key === 'actions' && !isSuperAdminOrCompanyAdmin) && !(col.key === TENANT_COL_KEY && !isAll));

  return (
    <Table
      size={'large'}
      columns={columns}
      dataSource={props.data}
      loading={props.loading}
      rowKey={(record) => record.uuid}
      pagination={props.pagination}
      onChange={onChange}
      onRow={(record) => {
        return {
          onClick: () => {
            props.onRowClick(record);
          },
        };
      }}
      locale={{ emptyText: <EmptyState title={tCommon('emptyTable')} /> }}
    />
  );
};

export default InstallationLocationTable;
